import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from './createBaseQuery';

export interface ChatHero {
  title: string;
  description: string;
  coverUrl?: string;
  buttonLabel?: string;
  href?: string;
}

export interface ChatHeading {
  title: string;
  description: string;
  coverUrl: string;
}

export interface ChatAdvantage {
  title: string;
  description: string;
}

export interface ChatScenarioGenerator {
  title: string;
  description: string;
  coverUrl?: string;
}

export interface ChatSignUpBlock {
  title: string;
  description: string;
}

export interface ChatHowItWorks {
  title: string;
  description: string;
}

export interface ChatFAQ {
  question: string;
  answer: string;
}

export interface ChatScenario {
  id: string;
  name: string;
  alias: string;
  publicName: string;
  publicDescription: string;
  publicRating: string;
  isFree: boolean;
  coverUrl: string;
  hasPhotos: boolean;
  likesNumber: number;
  tags: string[];
  videoCoverUrl?: string;
  isPublic: boolean;
  active: boolean;
}

export interface IChat {
  id: string;
  name: string;
  title?: string;
  hero?: ChatHero;
  heading?: ChatHeading[];
  advantages?: ChatAdvantage[];
  scenarioGenerator?: ChatScenarioGenerator;
  scenarios?: ChatScenario[];
  signUpBlock?: ChatSignUpBlock;
  howItWorks?: ChatHowItWorks[];
  faq?: ChatFAQ[];
  chats?: ChatItem[];
}

export interface ChatItem {
  id: string;
  name: string;
  title: string;
}

export interface Chat {
  id: number;
  name: string;
  title?: string;
  hero?: ChatHero;
  heading?: ChatHeading[];
  advantages?: ChatAdvantage[];
  scenarioGenerator?: ChatScenarioGenerator;
  scenarios?: ChatScenario[];
  signUpBlock?: ChatSignUpBlock;
  howItWorks?: ChatHowItWorks[];
  faq?: ChatFAQ[];
  chats?: ChatItem[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateChatRequest {
  name: string;
  title: string;
  hero: ChatHero;
  heading: ChatHeading[];
  advantages: ChatAdvantage[];
  scenarioGenerator: ChatScenarioGenerator;
  signUpBlock: ChatSignUpBlock;
  howItWorks: ChatHowItWorks[];
  faq: ChatFAQ[];
  chats?: ChatItem[];
}

export interface UpdateChatRequest {
  name?: string;
  title?: string;
  hero?: ChatHero;
  heading?: ChatHeading[];
  advantages?: ChatAdvantage[];
  scenarioGenerator?: ChatScenarioGenerator;
  signUpBlock?: ChatSignUpBlock;
  howItWorks?: ChatHowItWorks[];
  faq?: ChatFAQ[];
  chats?: ChatItem[];
}

export const chatsApi = createApi({
  reducerPath: 'chatsApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['Chats'],
  endpoints: (builder) => ({
    getChats: builder.query<Chat[], void>({
      query: () => ({
        url: '/chats',
        method: 'GET'
      }),
      providesTags: ['Chats']
    }),
    getChatByName: builder.query<Chat, string>({
      query: (chatName) => ({
        url: `/chats/${chatName}`,
        method: 'GET'
      }),
      providesTags: ['Chats']
    }),
    createChat: builder.mutation<Chat, CreateChatRequest>({
      query: (chatData) => ({
        url: '/admin/chats',
        method: 'POST',
        body: chatData
      }),
      invalidatesTags: ['Chats']
    }),
    updateChatByName: builder.mutation<Chat, { name: string; data: UpdateChatRequest }>({
      query: ({ name, data }) => ({
        url: `/admin/chats/${name}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['Chats']
    }),
    deleteChatByName: builder.mutation<void, string>({
      query: (chatName) => ({
        url: `/admin/chats/${chatName}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Chats']
    })
  })
});

export const {
  useGetChatsQuery,
  useLazyGetChatsQuery,
  useGetChatByNameQuery,
  useLazyGetChatByNameQuery,
  useCreateChatMutation,
  useUpdateChatByNameMutation,
  useDeleteChatByNameMutation
} = chatsApi; 